<script setup>
import {useHeadService} from '~/composables/useHeadService';
import {useAuthService} from '~/composables/useAuthService';
import FixtureResultRow from "~/components/fixtures/FixtureResultRow.vue";
import FrontendHeader from "~/components/layout/FrontendHeader.vue";
import {useRoute} from "vue-router";

definePageMeta({
  name: 'FixtureSearch',
});
const { t } = useI18n()
const {setMeta} = useHeadService();
setMeta(
    t('fixtures', 0),
    'Home page'
);
const {isAuthenticated, user, logout, isSuperUser} = useAuthService();
const route = useRoute();
const filterValues = ref(route.query);

const headerActions = (instance) => {
  let actions = [];
  if (!user.value.id) return actions;

  // The API allows this export to super admins, and to club admins for their own
  // clubs only (ExportAccess, 24 Sep 2026), so only offer it where it will work.
  const adminClubs = user.value.adminFor ?? [];
  const filteredClubs = filterValues.value?.clubIds == null
    ? []
    : [].concat(filterValues.value.clubIds);
  const exportAllowed = filteredClubs.length > 0
    && filteredClubs.every(clubId => adminClubs.includes(clubId));

  if (exportAllowed || isSuperUser.value) {
    actions.push({
      icon: 'mdi-export',
      title: 'Export',
      color: 'info',
      click: instance.exportAction,
      loading: instance.exportPending,
    });
  }

  return actions;
};

</script>

<template>
  <base-page>
    <v-container class="large">
      <crud-paginated-loader
          :allow-create="false"
          path="api/v2/fixtures"
          :custom-filters="{isPlayed: true}"
          :listeners="['fixtureUpdated']"
          :perPage="10"
          :show-per-page-selector="false"
          :await="true"
      >
        <template #before="instance">
          <frontend-header
              class="mb-4"
              title="Matches"
              :sub-title="t('search_fixtures')"
              :actions="headerActions(instance)"
              :hide-breadcrumbs="true"
          >
          </frontend-header>
        </template>
        <template #default="{items, totalItems, totalPages, currentPage, listPending, listErrors, hasListErrors}">
          <div class="fixture-list">
            <fixture-result-row
                v-for="fixture in items"
                :key="fixture.id"
                :fixture="fixture"
            />
          </div>
        </template>
      </crud-paginated-loader>
    </v-container>
  </base-page>
</template>

<style scoped lang="scss">
/* One flat list of fixture cards — gap between cards, no extra margin */
.fixture-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* FixtureCell already uses .tl-card; strip any legacy margin-bottom
   the component adds so the gap owns all spacing */
.fixture-list :deep(.fixture-card) {
  margin-bottom: 0 !important;
}
</style>
